import { memo, useRef } from "react";
import * as Styled from "./style";
import { useEpisode } from "./use-episode";
import { useRouter } from "next/router";
import { EpisodeTag } from "~/components/blocks/product/common/blocks/episode-tag";
import { ItemEpisode } from "~/components/blocks/product/common/blocks/item-episode";
import { ItemEpisodeDetailSkeleton } from "~/components/blocks/product/common/blocks/item-episode/detail-skeleton";
import { TagList } from "~/components/blocks/product/common/blocks/tag-list";
import { PATHS } from "~/const/paths";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import type { G_VideoSeasonType } from "~/lib/graphql";
import { G_VideoPpvExpirationType } from "~/lib/graphql";
import { calculatePlayProgressRate } from "~/util/calculate-play-progress-rate";
import { durationTimeText } from "~/util/duration-time-text";
import { formatDateToYMDHm } from "~/util/format-date";
import { goToWelcomePage } from "~/util/go-to-welcome-page";

/** undefinedの時は何も表示しない */
let episodeTabPurchasedText: "購入" | "その他の購入方法" | undefined;
let episodeTabPpvExpirationText: string | undefined;

/** 20話以上エピソードがなかったら`CarouselTag`を表示しない */
const MAX_TOTAL_EPISODE_LENGTH = 20;

type EpisodeProps = {
  /** シーズンタイプ（購入モーダルのUI構築のために必要） */
  seasonType: G_VideoSeasonType;
  /** エピソードの合計数 */
  totalEpisodeLength: number;
  /**
   * エピソード末端を開く場合に使用。`after`に指定するための数値（デフォルト値は`1`）
   * - クエリパラメータにcontentが付与されていた場合 : episodeNumberの値
   * - クエリパラメータにcontentが付与されていない場合 : 1
   */
  episodeNumber?: number;
};

/**
 * エピソード一覧を表示するコンポーネント（複数話とスポット配信で使用）
 * @param props {@link EpisodeProps}
 */
export const Episode = memo((props: EpisodeProps) => {
  const { seasonType, totalEpisodeLength, episodeNumber = 1 } = props;

  const { push } = useRouter();
  const episodeTagListRef = useRef<HTMLDivElement | null>(null);

  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "LANDSCAPE" });

  const {
    activeIndex,
    itemHeight,
    imageWidth,
    imageHeight,
    episodeTagListArray,
    isVideoMainEpisodeLoading,
    mainEpisodeEdges,
    episodeDownloadBtnClicked,
    episodePurchaseBtnClicked,
  } = useEpisode({ episodeNumber, totalEpisodeLength });

  return (
    <Styled.DivEpisodeRoot existEpisodeTab={totalEpisodeLength > MAX_TOTAL_EPISODE_LENGTH}>
      {totalEpisodeLength > MAX_TOTAL_EPISODE_LENGTH && (
        <Styled.DivTagListCarouselBox ref={episodeTagListRef}>
          <TagList activeIndex={activeIndex} tagListArray={episodeTagListArray} />
        </Styled.DivTagListCarouselBox>
      )}

      {!isVideoMainEpisodeLoading && mainEpisodeEdges !== undefined ? (
        <>
          {mainEpisodeEdges.map(({ node }, index) => {
            const {
              freeProduct,
              svodProduct,
              ppvProducts,
              ppvExpiration,
              viewingRights,
              priceSummary,
              playInfo: { contentId, parts, duration },
            } = node;

            // 購入とその他の購入方法の出し分けのフロー
            if (!node.viewingRights.isStreamable) {
              if (ppvProducts.length === 0) episodeTabPurchasedText = undefined;
              if (ppvProducts.length > 0) episodeTabPurchasedText = "購入";
            } else {
              const isAllPurchased = ppvProducts.every((cur) => cur.isPurchased);
              const isNotAllPurchased = ppvProducts.every((cur) => !cur.isPurchased);

              if (ppvProducts.length === 0) episodeTabPurchasedText = undefined;
              if (ppvProducts.length === 1 && isAllPurchased) episodeTabPurchasedText = undefined;
              if (ppvProducts.length > 1 && isAllPurchased) episodeTabPurchasedText = undefined;
              if (ppvProducts.length > 1 && !isAllPurchased && !isNotAllPurchased)
                episodeTabPurchasedText = "その他の購入方法";
              if (ppvProducts.length > 0 && isNotAllPurchased) episodeTabPurchasedText = "その他の購入方法";
            }

            // 視聴期限とその出し分けのフロー
            if (ppvExpiration !== undefined && ppvExpiration !== null) {
              const { expirationType, viewingExpiration, viewingStartExpiration, startDeliveryAt } = ppvExpiration;

              if (expirationType === G_VideoPpvExpirationType.Est) {
                episodeTabPpvExpirationText = "視聴期限：無期限";
              }

              if (expirationType === G_VideoPpvExpirationType.TvodDynamicExpiration) {
                if (!viewingExpiration) {
                  if (viewingStartExpiration) {
                    episodeTabPpvExpirationText = `${formatDateToYMDHm(
                      viewingStartExpiration
                    )}までに視聴開始してください`;
                  }
                } else {
                  episodeTabPpvExpirationText = `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
                }
              }

              if (expirationType === G_VideoPpvExpirationType.TvodFixedExpiration) {
                if (startDeliveryAt && viewingExpiration) {
                  /** クライアントの時刻が 配信開始日より前 の場合 */
                  const isBeforeDeliveryDate = new Date(startDeliveryAt).getTime() > Date.now();

                  if (isBeforeDeliveryDate) {
                    episodeTabPpvExpirationText = `視聴期限：${formatDateToYMDHm(
                      startDeliveryAt
                    )} - ${formatDateToYMDHm(viewingExpiration)}`;
                  } else {
                    // クライアントの時刻が 配信開始日より後 の場合
                    episodeTabPpvExpirationText = `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
                  }
                }
              }
            } else {
              // 条件に当てはまらなかった場合は「undefined」を代入する
              episodeTabPpvExpirationText = undefined;
            }

            const part = parts.find((part) => part.contentId === contentId);

            const progressBarRate =
              part !== undefined &&
              part?.resumePoint !== undefined &&
              part?.resumePoint !== null &&
              part?.resumePoint !== 0
                ? calculatePlayProgressRate(part.resumePoint, part.duration)
                : undefined;

            const isVisibleFree = freeProduct !== undefined && freeProduct !== null && freeProduct.isBeingDelivered;
            const isVisibleSvod = svodProduct !== undefined && svodProduct !== null && svodProduct.isBeingDelivered;
            const isVisiblePpv = ppvProducts.length > 0 && ppvProducts[0].isBeingDelivered;

            return (
              <ItemEpisode
                key={`ItemEpisode-${index}`}
                {...imageSrcFormat({ imgSrc: node.episodeImage })}
                alt={node.episodeTitle}
                episodeImageWidth={imageWidth}
                episodeImageHeight={imageHeight}
                tagComponent={
                  <EpisodeTag
                    freeProduct={freeProduct}
                    svodProduct={svodProduct}
                    priceSummary={priceSummary}
                    ppvExpiration={ppvExpiration}
                  />
                }
                episodeNumberName={node.episodeNumberName}
                episodeTitle={node.episodeTitle}
                episodeDescription={node.episodeDetail}
                purchasedText={episodeTabPurchasedText}
                imgSupplementText={durationTimeText(duration)}
                supplementText={episodeTabPpvExpirationText}
                aspectRatio="9/16"
                visiblePlayIcon={isVisibleFree || isVisibleSvod || isVisiblePpv}
                isDownloadIcon={viewingRights.isDownloadable}
                // resumePointの値が「0」または「undefined」の場合は「レジュームバー」を表示しないように制御
                playedProgressRate={progressBarRate}
                onIconClick={() => {
                  const downloadModalData = {
                    titleName: node.episodeTitle,
                    seasonType,
                    downloadableFiles: viewingRights.downloadableFiles,
                  };

                  episodeDownloadBtnClicked(node.id, downloadModalData);
                }}
                onPurchasedBtnClick={() => episodePurchaseBtnClicked(node.id)}
                onThumbnailClick={async () => {
                  // 視聴権利なしの場合
                  if (!viewingRights.isStreamable) {
                    // PPV取扱いありの場合、購入モーダル開く、PPV取扱いなしの場合、ウェルカムページに画面遷移
                    if (ppvProducts !== null && ppvProducts !== undefined && ppvProducts.length > 0) {
                      episodePurchaseBtnClicked(node.id);
                    } else {
                      goToWelcomePage();
                    }

                    return;
                  }

                  // 視聴権ありの場合、プレイヤー画面に遷移して動画を再生すること
                  void push(
                    {
                      pathname: PATHS.VOD_PLAYER,
                      query: {
                        season: node.seasonId,
                        content: node.id,
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    }
                  );
                }}
              />
            );
          })}
        </>
      ) : (
        <ItemEpisodeDetailSkeleton
          skeletonLength={20}
          oneItemWidthRatio={100}
          itemHeight={itemHeight}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        />
      )}
      {!isVideoMainEpisodeLoading &&
        episodeTagListArray.length !== activeIndex + 1 &&
        totalEpisodeLength > MAX_TOTAL_EPISODE_LENGTH && (
          <Styled.DivTagListCarouselBox>
            <TagList
              activeIndex={activeIndex}
              tagListArray={episodeTagListArray}
              resetScrollPosition={() => {
                if (episodeTagListRef.current) {
                  episodeTagListRef.current.scrollIntoView({ block: "start" });
                }
              }}
            />
          </Styled.DivTagListCarouselBox>
        )}
    </Styled.DivEpisodeRoot>
  );
});

Episode.displayName = "Episode";
